import type { TextStyle } from '../state/types';
import { Connector } from '../shapes/Connector';
import { Shape } from '../shapes/Shape';
import { TextElement } from '../shapes/TextElement';
import { PADDING } from '../../../config/constants';
import { parseTextLine, formatLine } from '../textEditor/TextFormatter';
import type { LineFormatType } from '../textEditor/TextFormatter';
import {
  createConnectorTextOverlay,
  createShapeTextOverlay,
  createTextElementOverlay,
  removeTextOverlay,
} from '../textEditor/Overlay';
import {
  applyConnectorOverlayPosition,
  applyShapeOverlayLayout,
  getConnectorOverlayAnchor,
  getShapeOverlayLayout,
  getTextElementOverlayLayout,
} from '../textEditor/Positioning';
import {
  continueListOnShiftEnter,
  handleToolbarBlur,
  handleTabIndent,
  handleSmartEnter,
  handleAutoFormat,
} from '../textEditor/InputBehavior';

type DiagramElement = Shape | Connector | TextElement;

export interface DiagramTextEditorContext {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  getScale: () => number;
  getPan: () => { panX: number; panY: number };
  isInverted: () => boolean;
  render: () => void;
  notifyChange: () => void;
  updatePlaceholder: () => void;
  removeTextElement: (textElement: TextElement) => void;
}

export class DiagramTextEditor {
  private readonly context: DiagramTextEditorContext;
  private textInput: HTMLTextAreaElement | null = null;
  private editingShape: Shape | null = null;
  private editingConnector: Connector | null = null;
  private editingTextElement: TextElement | null = null;

  constructor(context: DiagramTextEditorContext) {
    this.context = context;
  }

  isEditing(): boolean {
    return this.textInput !== null;
  }

  getCurrentInputValue(): string | null {
    if (!this.textInput) return null;
    return this.textInput.value;
  }

  getEditingShape(): Shape | null {
    return this.editingShape;
  }

  getEditingConnector(): Connector | null {
    return this.editingConnector;
  }

  getEditingTextElement(): TextElement | null {
    return this.editingTextElement;
  }

  editElement(element: DiagramElement): void {
    if (element instanceof TextElement) {
      this.editTextElementText(element);
      return;
    }

    if (element instanceof Shape) {
      this.editShapeText(element);
      return;
    }

    this.editConnectorText(element as Connector);
  }

  close(): void {
    const input = this.textInput;
    if (!input) return;

    const inputValue = input.value;
    this.textInput = null;

    if (this.editingShape) {
      this.editingShape.text = inputValue;
      this.editingShape = null;
    } else if (this.editingConnector) {
      this.editingConnector.text = inputValue;
      this.editingConnector = null;
    } else if (this.editingTextElement) {
      const newText = (inputValue ?? '').trim();
      if (newText) {
        this.editingTextElement.text = newText;
      } else {
        this.removeTextElement(this.editingTextElement);
      }
      this.editingTextElement = null;
    }

    removeTextOverlay(input);
    this.context.notifyChange();
    this.context.render();
  }

  updatePosition(): void {
    const input = this.textInput;
    if (!input) return;

    if (this.editingShape) {
      const layout = getShapeOverlayLayout(this.context, this.editingShape);
      applyShapeOverlayLayout(input, layout);
      return;
    }

    if (this.editingConnector) {
      const anchor = getConnectorOverlayAnchor(this.context, this.editingConnector);
      applyConnectorOverlayPosition(input, anchor);
    }
  }

  private attachTextOverlay(input: HTMLTextAreaElement, selectOnFocus: boolean): void {
    document.body.appendChild(input);
    input.focus();
    if (selectOnFocus) {
      input.select();
    }
  }

  private finalizeTextEdit<T>(
    target: T | null,
    clearTarget: () => void,
    commit: (value: string, target: T) => void
  ): void {
    const input = this.textInput;
    const inputValue = input ? input.value : '';
    this.textInput = null;
    clearTarget();
    if (target) {
      commit(inputValue, target);
    }
    removeTextOverlay(input);
  }

  private bindTextInputHandlers(
    input: HTMLTextAreaElement,
    options: {
      onFinish: () => void;
      onEscape: () => void;
      allowShiftEnterList?: boolean;
    }
  ): void {
    const allowShiftEnterList = options.allowShiftEnterList ?? false;

    input.addEventListener('blur', (e: FocusEvent): void => {
      handleToolbarBlur(e, options.onFinish, () => this.textInput?.focus());
    });
    input.addEventListener('keydown', (e: KeyboardEvent): void => {
      e.stopPropagation();

      // Tab/Shift+Tab for indentation
      if (e.key === 'Tab') {
        e.preventDefault();
        handleTabIndent(input, e.shiftKey);
        return;
      }

      // Escape exits editing
      if (e.key === 'Escape') {
        options.onEscape();
        return;
      }

      // Ctrl/Cmd+Enter finalizes text
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        options.onFinish();
        return;
      }

      // Enter inserts newline with smart list continuation
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSmartEnter(input);
        return;
      }

      // Legacy Shift+Enter list support (deprecated but kept for compatibility)
      if (allowShiftEnterList && e.key === 'Enter' && e.shiftKey) {
        if (continueListOnShiftEnter(input)) {
          e.preventDefault();
          return;
        }
      }
    });

    // Auto-format on Space after patterns
    input.addEventListener('keyup', (e: KeyboardEvent): void => {
      if (e.key === ' ') {
        handleAutoFormat(input);
      }
    });
  }

  private editShapeText(element: Shape): void {
    this.editingShape = element;
    const originalText = element.text;
    const scale = this.context.getScale();
    const layout = getShapeOverlayLayout(this.context, element);
    const basePadding = PADDING.shapeText * scale;
    const initialFontSize = originalText
      ? element.calculateFittingFontSize(this.context.ctx, originalText) * scale
      : element.textStyle.fontSize * scale;

    const overlayOptions = {
      value: originalText,
      left: layout.left,
      top: layout.top,
      width: layout.width,
      height: layout.height,
      initialFontSize,
      textStyle: element.textStyle,
      shapeType: element.type,
      verticalPadding: basePadding,
      inverted: this.context.isInverted(),
    };

    this.textInput = createShapeTextOverlay(overlayOptions);

    const input = this.textInput;
    if (!input) return;

    element.text = '';
    this.context.render();

    this.attachTextOverlay(input, false);

    const updateFontSize = (): void => {
      if (this.editingShape && input) {
        const fittingSize = this.editingShape.calculateFittingFontSize(this.context.ctx, input.value);
        input.style.fontSize = `${fittingSize * this.context.getScale()}px`;
      }
    };
    input.addEventListener('input', updateFontSize);

    const finishEditing = (): void => {
      this.finalizeTextEdit(this.editingShape, () => {
        this.editingShape = null;
      }, (value, editingShape) => {
        editingShape.text = value;
        this.context.notifyChange();
        this.context.render();
      });
    };
    const handleEscape = (): void => {
      const editingShape = this.editingShape;
      this.textInput = null;
      this.editingShape = null;
      if (editingShape) {
        editingShape.text = originalText;
      }
      removeTextOverlay(input);
      this.context.render();
    };

    this.bindTextInputHandlers(input, {
      allowShiftEnterList: true,
      onFinish: finishEditing,
      onEscape: handleEscape,
    });
  }

  private editConnectorText(connector: Connector): void {
    this.editingConnector = connector;
    const scale = this.context.getScale();
    const anchor = getConnectorOverlayAnchor(this.context, connector);

    const fontSize = connector.textSize * scale;
    const inputWidth = Math.max(150, fontSize * 10);
    const inputHeight = Math.max(28, fontSize * 1.5 + 12);
    const left = anchor.x - inputWidth / 2;
    const top = anchor.y - inputHeight / 2;

    const overlayOptions = {
      value: connector.text,
      left,
      top,
      width: inputWidth,
      height: inputHeight,
      fontSize,
      inverted: this.context.isInverted(),
    };

    this.textInput = createConnectorTextOverlay(overlayOptions);

    const input = this.textInput;
    if (!input) return;

    this.attachTextOverlay(input, false);

    const finishEditing = (): void => {
      this.finalizeTextEdit(this.editingConnector, () => {
        this.editingConnector = null;
      }, (value, editingConnector) => {
        editingConnector.text = value;
        this.context.notifyChange();
        this.context.render();
      });
    };
    const handleEscape = (): void => {
      this.textInput = null;
      this.editingConnector = null;
      removeTextOverlay(input);
    };

    this.bindTextInputHandlers(input, {
      onFinish: finishEditing,
      onEscape: handleEscape,
    });
  }

  private editTextElementText(textElement: TextElement): void {
    this.editingTextElement = textElement;
    const scale = this.context.getScale();
    const layout = getTextElementOverlayLayout(this.context, textElement, this.context.ctx);
    const fixedScreenX = layout.left;
    const fixedScreenY = layout.top;

    const overlayOptions = {
      value: textElement.text,
      placeholder: 'Type...',
      left: layout.left,
      top: layout.top,
      width: layout.width,
      height: layout.height,
      fontSize: layout.fontSize,
      textStyle: textElement.textStyle,
      inverted: this.context.isInverted(),
      wrapText: !textElement.autoResize && textElement.maxWidth !== undefined,
      scale,
    };

    this.textInput = createTextElementOverlay(overlayOptions);

    const input = this.textInput;
    if (!input) return;

    const originalText = textElement.text;
    textElement.text = '';
    this.context.render();

    this.attachTextOverlay(input, false);

    const updateSize = (): void => {
      if (this.editingTextElement && input) {
        this.editingTextElement.text = input.value;
        const newBounds = this.editingTextElement.getBounds(this.context.ctx);
        this.editingTextElement.text = '';

        // Dimensions are unscaled — CSS transform: scale() handles zoom
        const bufferHeight = 20;

        if (this.editingTextElement.maxWidth && !this.editingTextElement.autoResize) {
          const newHeight = Math.max(40, newBounds.height + bufferHeight);
          input.style.height = `${newHeight}px`;
        } else {
          const bufferWidth = 50;
          const newWidth = Math.max(200, newBounds.width + bufferWidth);
          const newHeight = Math.max(40, newBounds.height + bufferHeight);
          input.style.width = `${newWidth}px`;
          input.style.height = `${newHeight}px`;
        }
        input.style.left = `${fixedScreenX}px`;
        input.style.top = `${fixedScreenY}px`;
      }
    };
    input.addEventListener('input', updateSize);

    const finishEditing = (): void => {
      this.finalizeTextEdit(this.editingTextElement, () => {
        this.editingTextElement = null;
      }, (value, editingTextElement) => {
        const newText = value.trim();
        if (newText) {
          editingTextElement.text = newText;
        } else {
          this.removeTextElement(editingTextElement);
        }
        this.context.notifyChange();
        this.context.render();
      });
    };
    const handleEscape = (): void => {
      const editingTextElement = this.editingTextElement;
      this.textInput = null;
      this.editingTextElement = null;
      if (editingTextElement) {
        if (!originalText) {
          this.removeTextElement(editingTextElement);
        } else {
          editingTextElement.text = originalText;
        }
      }
      removeTextOverlay(input);
      this.context.render();
    };

    this.bindTextInputHandlers(input, {
      allowShiftEnterList: true,
      onFinish: finishEditing,
      onEscape: handleEscape,
    });
  }

  private removeTextElement(textElement: TextElement): void {
    this.context.removeTextElement(textElement);
    this.context.updatePlaceholder();
  }

  applyTextStyle(style: Partial<TextStyle>): void {
    const input = this.textInput;
    if (!input) return;
    const scale = this.context.getScale();

    if (this.editingShape) {
      this.editingShape.textStyle = { ...this.editingShape.textStyle, ...style };
      input.style.fontFamily = this.editingShape.textStyle.fontFamily;
      input.style.fontWeight = this.editingShape.textStyle.fontWeight;
      input.style.fontStyle = this.editingShape.textStyle.fontStyle;
      input.style.textAlign = this.editingShape.textStyle.textAlign;
      input.style.color = this.editingShape.textStyle.color;

      const fittingSize = this.editingShape.calculateFittingFontSize(
        this.context.ctx,
        input.value
      );
      input.style.fontSize = `${fittingSize * scale}px`;
      return;
    }

    if (this.editingTextElement) {
      this.editingTextElement.textStyle = { ...this.editingTextElement.textStyle, ...style };
      input.style.fontFamily = this.editingTextElement.textStyle.fontFamily;
      input.style.fontWeight = this.editingTextElement.textStyle.fontWeight;
      input.style.fontStyle = this.editingTextElement.textStyle.fontStyle;
      input.style.textAlign = this.editingTextElement.textStyle.textAlign;
      input.style.color = this.editingTextElement.textStyle.color;
      // Font size is unscaled — CSS transform: scale() handles zoom
      input.style.fontSize = `${this.editingTextElement.textStyle.fontSize}px`;

      // Temporarily set text to measure accurate bounds for overlay sizing
      this.editingTextElement.text = input.value;
      const layout = getTextElementOverlayLayout(this.context, this.editingTextElement, this.context.ctx);
      this.editingTextElement.text = '';

      input.style.left = `${layout.left}px`;
      input.style.top = `${layout.top}px`;
      input.style.width = `${layout.width}px`;
      input.style.height = `${layout.height}px`;
      return;
    }

    if (this.editingConnector && style.fontSize !== undefined) {
      this.editingConnector.textSize = style.fontSize;
      const fontSize = this.editingConnector.textSize * scale;
      const inputWidth = Math.max(150, fontSize * 10);
      const inputHeight = Math.max(28, fontSize * 1.5 + 12);
      input.style.fontSize = `${fontSize}px`;
      input.style.width = `${inputWidth}px`;
      input.style.height = `${inputHeight}px`;
    }
  }

  insertListPrefix(kind: 'ordered' | 'unordered'): void {
    const input = this.textInput;
    if (!input) return;

    const value = input.value;
    const selectionStart = input.selectionStart ?? value.length;
    const selectionEnd = input.selectionEnd ?? value.length;
    const lineStart = value.lastIndexOf('\n', selectionStart - 1) + 1;
    const lineEndIndex = value.indexOf('\n', selectionEnd);
    const lineEnd = lineEndIndex === -1 ? value.length : lineEndIndex;
    const currentLine = value.slice(lineStart, lineEnd);

    const format = parseTextLine(currentLine);

    // Toggle off if already formatted with same type
    if (kind === 'ordered' && format.type === 'ordered') return;
    if (kind === 'unordered' && format.type === 'unordered') return;

    // Apply formatting using helper
    const insertAt = lineStart + (format.indent * 2); // Convert indent level to spaces
    const prefix = kind === 'ordered' ? '1. ' : '- ';
    const before = value.slice(0, insertAt);
    const after = value.slice(insertAt);
    input.value = `${before}${prefix}${after}`;

    const newStart = selectionStart + prefix.length;
    const newEnd = selectionEnd + prefix.length;
    input.selectionStart = newStart;
    input.selectionEnd = newEnd;
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }

  applyLineFormat(type: LineFormatType): void {
    const input = this.textInput;
    if (!input) return;

    const value = input.value;
    const selectionStart = input.selectionStart ?? value.length;
    const selectionEnd = input.selectionEnd ?? value.length;
    const lineStart = value.lastIndexOf('\n', selectionStart - 1) + 1;
    const lineEndIndex = value.indexOf('\n', selectionEnd);
    const lineEnd = lineEndIndex === -1 ? value.length : lineEndIndex;
    const currentLine = value.slice(lineStart, lineEnd);

    const format = parseTextLine(currentLine);

    // Format the line with the new type, preserving indent
    const newLine = formatLine(format.content, type, format.indent, format.listNumber);

    // Replace the current line
    const before = value.slice(0, lineStart);
    const after = value.slice(lineEnd);
    input.value = `${before}${newLine}${after}`;

    // Calculate cursor position delta
    const lengthDelta = newLine.length - currentLine.length;
    const newStart = Math.max(lineStart, selectionStart + lengthDelta);
    const newEnd = Math.max(lineStart, selectionEnd + lengthDelta);

    input.selectionStart = newStart;
    input.selectionEnd = newEnd;
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }
}
